import { DOCUMENT, registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import ar from '@angular/common/locales/ar';
import en from '@angular/common/locales/en';
import { ApplicationRef, Inject, Injectable } from '@angular/core';
import { TranslateService as ngxTranslate } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, map, shareReplay } from 'rxjs';
import { ApiConstants } from '../constants/api.constants';

export enum LANGUAGES {
    AR = 'ar',
    EN = 'en'
}

export enum DIRECTIONS {
    RTL = 'rtl',
    LTR = 'ltr'
}

@Injectable({
    providedIn: 'root'
})
export class TranslateService {
    public currentLanguage: BehaviorSubject<string> = new BehaviorSubject(null);
    constructor(
        private http: HttpClient,
        private readonly ngxTranslate: ngxTranslate,
        private cookie: CookieService,
        @Inject(DOCUMENT) private document: Document,
        private readonly applicationRef: ApplicationRef
    ) {}

    private setCurrentLang(lang: string): void {
        this.currentLanguage.next(lang);
    }

    public instant(key: string | Array<string>): any {
        return this.ngxTranslate.instant(key);
    }

    public get(key: string | Array<string>): Observable<any> {
        return this.ngxTranslate.get(key);
    }

    public getTranslation(lang: string): Observable<any> {
        return this.ngxTranslate.getTranslation(lang);
    }

    public getStreamOnTranslationChange(key: string): Observable<any> {
        return this.ngxTranslate.getStreamOnTranslationChange(key);
    }

    private saveLangToCookie(lang: string): void {
        this.cookie.set('lang', lang, null, '/');
        this.cookie.set('user-preferred-language', lang, null, '/');
    }

    public get currentLang$(): Observable<string> {
        return this.currentLanguage.asObservable().pipe(shareReplay(1));
    }

    public getLangFromCookie(): string {
        return this.cookie.get('lang');
    }

    public get otherLang$(): Observable<string> {
        return this.currentLanguage.asObservable().pipe(
            map(lang => {
                return lang === LANGUAGES.AR ? LANGUAGES.EN : LANGUAGES.AR;
            }),
            shareReplay(1)
        );
    }

    public setLang(lang?: string | null): void {
        const language: string = lang || this.ngxTranslate.currentLang || LANGUAGES.AR;
        this.setupNgxTranslate(language);
        this.saveLangToCookie(language);
        this.setupGlobalLocale(language);
        this.setupLanguageDirection(language);
        this.setCurrentLang(language);
        this.applicationRef.tick();
    }

    private setupNgxTranslate(language: string): void {
        this.ngxTranslate.use(language);
    }

    private setupGlobalLocale(language: string): void {
        registerLocaleData(language === LANGUAGES.AR ? ar : en);
       // moment.locale(language);
    }

    private setupLanguageDirection(language: string): void {
        const direction = language === LANGUAGES.AR ? DIRECTIONS.RTL : DIRECTIONS.LTR;
        const oppositeDirection = language === LANGUAGES.AR ? DIRECTIONS.LTR : DIRECTIONS.RTL;
        this.document.documentElement.lang = language;
        this.document.body.dir = direction;
        this.document.body.classList.remove(oppositeDirection);
        this.document.body.classList.add(direction);
    }

    public get languages(): { name: string; value: string }[] {
        return Object.keys(LANGUAGES).map(key => ({
            name: key,
            value: LANGUAGES[key]
        }));
    }

    public changeLang(key: string): void {
        this.http.patch(`${ApiConstants.USER_LANG}`, {}).subscribe();
        this.setLang(key);
    }
}
